// Ported from src/pages/System/RescAlertDevicesWidget.js on 2026-10-05.
import type {
  DeviceControlDraft,
  DeviceControlEventHandler,
} from 'features/Integrations/types/deviceControl.types';
import type { RescAlertDevicesWidgetProps } from './rescAlertDevicesWidget.types';
import { LayoutSizeContext } from 'App';
import { usePerms } from 'contexts/PermsContext';
import DeviceControls from 'features/Integrations/components/DeviceControls/DeviceControls';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import LegacyDeviceMap from 'features/Integrations/features/ConnectedDevices/features/DeviceMap/index.web.jsx';
import type { DeviceMapViewComponent } from 'features/Integrations/features/ConnectedDevices/types/deviceMapView.types';
import { ALL_DEVICE_TYPES_VISIBLE } from 'features/Integrations/state/slices/integrationSlice';
import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/Integrations/utils/deviceComparison.utils';
import { canViewDeviceType } from 'features/Integrations/utils/integrationVisibility.utils';
import { useContext, useEffect, useMemo, useState } from 'react';
import { sendDeviceCommand } from 'services/devices/commands/commands.service';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';

const DeviceMap = LegacyDeviceMap as DeviceMapViewComponent;

export default function RescAlertDevicesWidget({
  type = '',
  dimensions,
  nested = false,
  deviceForWidget = null,
}: RescAlertDevicesWidgetProps) {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery(undefined);
  const perms = usePerms();
  const layout = useContext(LayoutSizeContext);
  const [selectedDevice, setSelectedDeviceIndex] = useState(0);
  const [updatedSelectedDevice, setUpdatedSelectedDevice] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [viewImageInWidget, setViewImageInWidget] = useState(false);
  const [mapWidth, setMapWidth] = useState(501);
  const [mapHeight, setMapHeight] = useState(451);
  const [customDataOverride, setCustomDataOverride] = useState(null);
  const [useThisDevice, setUseThisDevice] = useState<DeviceControlDraft | null>(
    deviceForWidget || null,
  );
  const [devicesLoaded, setDevicesLoaded] = useState(false);

  // Minimum width (in px) for the DeviceControls area to keep controls usable
  const controlsMinWidth = 320;

  const setSelectedDevice = (index: number) => {
    setSelectedDeviceIndex(index);
    setUpdatedSelectedDevice(!updatedSelectedDevice);
  };

  const [displayDevice, setDisplayDevice] = useState(useThisDevice);
  const [initialized, setInitialized] = useState(false);
  const [sending, setSending] = useState(false);
  const [audioOverride, setAudioOverride] = useState(null);
  const [audioManager, setAudioManager] = useState(false);

  useEffect(() => {
    setDisplayDevice(useThisDevice);
  }, [useThisDevice]);

  const deviceControlListener: DeviceControlEventHandler = async (evt, data) => {
    switch (evt) {
      case 'update':
        setDisplayDevice(data);
        setInitialized(true);
        return;
      case 'submit':
        setSending(true);
        try {
          const {
            id,
            node_id,
            system_type,
            device_type,
            status,
            maintenanceOverride,
            comments,
            interval,
            volume,
            repeat,
            audio,
          } = data;

          await sendDeviceCommand(
            { id, node_id, system_type, device_type },
            status === -1 ? 1 : status,
            comments,
            maintenanceOverride || false,
            {
              interval: interval || 0,
              volume,
              repeat,
              audio,
              immediate: status === -1,
            },
          );
        } catch (error) {
          console.error('Error sending device command:', error);
        }
        setSending(false);
        return;
      case 'cancel-audio':
        try {
          await sendDeviceCommand(
            {
              id: useThisDevice.id,
              node_id: useThisDevice.node_id,
              system_type: useThisDevice.system_type,
              device_type: useThisDevice.device_type,
            },
            7,
            '',
            data?.maintenanceOverride === true,
          );
        } catch (err) {
          console.error('Error sending device command:', err.message);
        }
        return;
      case 'audio-manager':
        setAudioManager(true);
        return;
      default:
        return;
    }
  };

  const ROUTE = 'getNodes';
  const deviceTypeToFilterToType = (deviceType: string) => {
    if (deviceType === 'stage') return 'Stage';
    if (deviceType === 'manualRoadClosure') return 'Manual Road Closure';
    if (deviceType === 'technologyBridgeFlashingLights') return 'Technology Bridge_Flashing Lights';
    if (deviceType === 'technologyBridgeSiren') return 'Technology Bridge_Siren/Giant Voice';
    return undefined;
  };

  const isStageFlood = type === 'stage';
  const widgetDeviceType = useMemo(() => deviceTypeToFilterToType(type), [type]);
  const widgetDevices = useMemo(() => {
    if (!connectedDevices?.length || !widgetDeviceType) return [];
    return connectedDevices
      .map((device, index) => ({ device, originalIndex: index }))
      .filter(
        ({ device }) =>
          DeviceIsDeviceType(device, widgetDeviceType) &&
          (!isStageFlood || DeviceIsSystemType(device, 'Flood')) &&
          // Permissions only: the map filter on the Connected Devices page is a display
          // choice and must not hide command controls on the dashboard.
          canViewDeviceType(device, perms, ALL_DEVICE_TYPES_VISIBLE),
      )
      .sort((a, b) => parseInt(a.device.node_id || 0, 10) - parseInt(b.device.node_id || 0, 10));
  }, [connectedDevices, perms, widgetDeviceType]);
  const widgetDeviceOptions = useMemo(
    () =>
      widgetDevices.map(({ device }) => ({
        value: device.id,
        label:
          (device.device_type !== 'Manual Road Closure' ? device.static_id + ' ' : '') +
          device.alias,
      })),
    [widgetDevices],
  );
  const handleWidgetDeviceChange = (deviceId: number) => {
    const selected = widgetDevices.find(({ device }) => device.id === deviceId);
    if (!selected) return;
    // setSelectedDevice still takes the position in the unfiltered connectedDevices list.
    setSelectedDevice(selected.originalIndex);
    setUseThisDevice(selected.device);
  };

  useEffect(() => {
    if (!connectedDevices) {
      return;
    }
    if (devicesLoaded) return;
    if (deviceForWidget) {
      const foundIndex = connectedDevices.findIndex((device) => device.id === deviceForWidget.id);
      if (foundIndex !== -1) {
        setSelectedDevice(foundIndex);
      } else {
        console.error('Device not found in context for widget:', deviceForWidget);
      }
      return;
    }

    //find first node of type, then set selectedDevice to that index
    const firstNodeOfType = connectedDevices.findIndex((device) => {
      return (
        DeviceIsDeviceType(device, widgetDeviceType) &&
        (!isStageFlood || DeviceIsSystemType(device, 'Flood'))
      );
    });

    if (firstNodeOfType != -1) setSelectedDevice(firstNodeOfType);
    //find node by id
    if (firstNodeOfType !== -1) {
      const nodeById = connectedDevices[firstNodeOfType];
      if (nodeById) {
        setUseThisDevice(nodeById);
      }
    }

    if (connectedDevices.length > 0) setDevicesLoaded(true);
  }, [connectedDevices]);

  const renderWidgetMap = () => {
    const hasVisibleWidgetDevices = widgetDevices.length > 0;

    if ((dimensions[0] || 0) >= 2 && dimensions[1] === 1) {
      return (
        <div>
          {!nested && <WidgetTimeUpdate route={ROUTE} realtime />}
          <div
            style={{
              height: layout.widgetHeight - (nested ? 0 : 30),
            }}
          >
            {
              <div style={{ display: 'flex', height: '100%', overflow: 'hidden', gap: 12 }}>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                  }}
                >
                  <DeviceMap
                    deviceTypeToFilter={type}
                    setUseThisDevice={setUseThisDevice}
                    setShowCurrentDevice={() => {}}
                    setShowDeviceSettings={() => {}}
                    filtersCompact={false}
                    enableFilters={false}
                    selectedDeviceOverride={useThisDevice}
                    widget
                  />
                </div>

                <div
                  style={{
                    minWidth: controlsMinWidth,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    height: '100%',
                    overflow: 'visible',
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 20,
                      flexShrink: 0,
                      marginBottom: 8,
                    }}
                  >
                    <BaseSelect
                      label="Device"
                      isSearchable
                      selectProps={{
                        options: widgetDeviceOptions,
                        value: findSelectOption(widgetDeviceOptions, useThisDevice?.id),
                        placeholder: 'Select',
                        isDisabled: widgetDeviceOptions.length === 0,
                        onChange: (option) => handleWidgetDeviceChange(Number(option?.value)),
                      }}
                    />
                  </div>

                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                  >
                    {hasVisibleWidgetDevices ? (
                      <DeviceControls
                        device={displayDevice}
                        on={deviceControlListener}
                        sending={sending}
                        audioOverride={audioOverride}
                        audioManagerButtonEnabled={false}
                      />
                    ) : (
                      <div>No Devices Found</div>
                    )}
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      );
    }

    if ((dimensions?.[1] || 0) >= 2) {
      return (
        <div className="h-100 w-100 d-flex flex-column">
          {!nested && <WidgetTimeUpdate route={ROUTE} realtime />}
          <div className="w-100 h-100">
            {
              <div className="h-100 w-100 d-flex flex-column">
                <div className="ratio ratio-4x3 w-100 flex-shrink-0">
                  <div className="rounded-3 overflow-hidden">
                    <DeviceMap
                      deviceTypeToFilter={type}
                      setUseThisDevice={setUseThisDevice}
                      setShowCurrentDevice={() => {}}
                      setShowDeviceSettings={() => {}}
                      filtersCompact={dimensions?.[0] === 1 && dimensions?.[1] === 2}
                      enableFilters={false}
                      selectedDeviceOverride={useThisDevice}
                      widget
                    />
                  </div>
                </div>

                <div className="mx-3 mt-2" style={{ position: 'relative', zIndex: 20 }}>
                  <BaseSelect
                    label="Device"
                    isSearchable
                    selectProps={{
                      options: widgetDeviceOptions,
                      value: findSelectOption(widgetDeviceOptions, useThisDevice?.id),
                      placeholder: 'Select',
                      isDisabled: widgetDeviceOptions.length === 0,
                      onChange: (option) => handleWidgetDeviceChange(Number(option?.value)),
                    }}
                  />
                </div>

                <div className="d-flex flex-column m-3 flex-grow-1" style={{ minHeight: 0 }}>
                  {hasVisibleWidgetDevices ? (
                    <DeviceControls
                      device={displayDevice}
                      on={deviceControlListener}
                      sending={sending}
                      audioOverride={audioOverride}
                      audioManagerButtonEnabled={false}
                    />
                  ) : (
                    <div>No Devices Found</div>
                  )}
                </div>
              </div>
            }
          </div>
        </div>
      );
    }
  };

  if (connectedDevices.length > 0) {
    return renderWidgetMap();
  }

  return 'Loading...';
}
